// Bundle picker: resolves the bundle's components for the selected tier
// (bundle variant) + bundle options, checks component stock, and writes the
// components onto the product form as line item properties so the bundle
// stays a single line through cart, checkout and the Admin order.
//
// Properties written (customers only see the option choice; the pack list
// stays staff-side via the b2b-app order block and packing slips):
//   <Option name>            visible   the customer's choice, e.g. Scent: Lavender
//   _bundle_contents         hidden    "2 × Title;\n1 × Title" per bundle (packing slip)
//   _bundle                  hidden    bundle product id
//   _bundle_items            hidden    "variantId:qty,..." per bundle (read by b2b-app)
if (!customElements.get('bundle-picker')) {
  customElements.define('bundle-picker', class BundlePicker extends HTMLElement {
    connectedCallback() {
      const items = this.readJson('[data-bundle-items]');
      // A null entry is an item the theme couldn't describe at all (older
      // markup). Items this shopper can't see arrive as { hidden: true } with
      // just a name: they still ship and b2b-app takes their stock from the
      // bundle's saved contents, so they're listed but never block the sale.
      this.unavailableCount = items.filter((item) => !item).length;
      this.items = items.filter(Boolean);
      this.variants = this.readJson('[data-bundle-variants]');
      this.form = document.getElementById(this.dataset.formId);
      this.sectionId = this.dataset.section;
      this.previewCount = parseInt(this.dataset.previewCount, 10) || 8;
      this.showImages = this.dataset.showImages === 'true';
      this.showSku = this.dataset.showSku === 'true';
      this.expanded = false;

      this.list = this.querySelector('[data-bundle-list]');
      this.toggle = this.querySelector('[data-bundle-toggle]');
      this.error = this.querySelector('[data-bundle-error]');
      this.propertiesContainer = this.querySelector('[data-bundle-properties]');
      this.fieldsets = Array.from(this.querySelectorAll('[data-option-name]'));

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-name] input[type="radio"]')) this.update();
      });
      this.toggle.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.render();
      });

      this.onSubmit = this.onSubmit.bind(this);
      this.onQuantityChange = (event) => {
        if (event.target.name === 'quantity' && event.target.getAttribute('form') === this.dataset.formId) this.update();
      };
      if (this.form) this.form.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('change', this.onQuantityChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event?.data?.variant;
          if (!variant || !this.variants.some((v) => v.id === variant.id)) return;
          // The theme has just set the button state for this variant; take it as the baseline.
          if (this.submitButton) delete this.submitButton.dataset.bundleDisabled;
          this.update(variant.id);
        });
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('change', this.onQuantityChange);
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    }

    readJson(selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '[]');
      } catch (e) {
        console.error('[bundle-picker] invalid bundle data', e);
        return [];
      }
    }

    normalize(value) {
      return String(value ?? '').trim().toLowerCase();
    }

    get variantId() {
      const input = this.form?.querySelector('[name="id"]');
      return input ? parseInt(input.value, 10) : this.variants[0]?.id;
    }

    get lineQuantity() {
      const input = document.querySelector(`input[name="quantity"][form="${this.dataset.formId}"]`) ||
        this.form?.querySelector('[name="quantity"]');
      return Math.max(1, parseInt(input?.value, 10) || 1);
    }

    getSelections() {
      const selections = {};
      this.fieldsets.forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) selections[fieldset.dataset.optionName] = checked.value;
      });
      return selections;
    }

    // Tier labels match the bundle variant's title or any of its option values,
    // so "Testers + 2" works whether it is the whole title or one option.
    tierLabels(variantId) {
      const variant = this.variants.find((v) => v.id === variantId) || this.variants[0];
      if (!variant) return [];
      return [variant.title, ...(variant.options || [])].map((label) => this.normalize(label));
    }

    resolveItems(variantId, selections) {
      const tiers = this.tierLabels(variantId);
      const selected = Object.entries(selections).map(([name, value]) => [this.normalize(name), this.normalize(value)]);

      return this.items.filter((item) => {
        if (item.tiers.length && !item.tiers.some((tier) => tiers.includes(this.normalize(tier)))) return false;
        if (!item.optionValue) return true;
        const value = this.normalize(item.optionValue);
        const name = this.normalize(item.optionName);
        return selected.some(([selName, selValue]) => selValue === value && (!name || selName === name));
      });
    }

    // Collapse duplicate variants (e.g. a tester listed for every tier) into one row.
    aggregate(items) {
      const byVariant = new Map();
      items.forEach((item) => {
        const key = item.variantId ?? `hidden:${item.title}`;
        const existing = byVariant.get(key);
        if (existing) existing.quantity += item.quantity;
        else byVariant.set(key, { ...item });
      });
      return Array.from(byVariant.values());
    }

    isShort(item, lineQuantity) {
      if (!item.available) return true;
      if (!item.tracked || item.continueSelling) return false;
      return item.stock < item.quantity * lineQuantity;
    }

    update(variantId = this.variantId) {
      this.currentVariantId = variantId;
      const selections = this.getSelections();
      const lineQuantity = this.lineQuantity;

      this.components = this.aggregate(this.resolveItems(variantId, selections));
      this.shortItems = this.components.filter((item) => this.isShort(item, lineQuantity));
      this.valid = this.components.length > 0 && this.shortItems.length === 0 && !this.unavailableCount;

      this.updateOptionAvailability(variantId, selections, lineQuantity);
      this.render();
      this.writeProperties(selections);
      this.updateSubmitButton();
    }

    updateOptionAvailability(variantId, selections, lineQuantity) {
      this.fieldsets.forEach((fieldset) => {
        const name = fieldset.dataset.optionName;
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const trial = this.aggregate(this.resolveItems(variantId, { ...selections, [name]: input.value }));
          const unavailable = trial.length === 0 || trial.some((item) => this.isShort(item, lineQuantity));
          input.classList.toggle('disabled', unavailable);
          const label = input.nextElementSibling;
          label?.classList.toggle('active', input.checked);
          label?.querySelector('[data-bundle-unavailable]')?.toggleAttribute('hidden', !unavailable);
        });
        const selectedLabel = fieldset.querySelector('[data-bundle-selected]');
        if (selectedLabel) selectedLabel.textContent = selections[name] || '';
      });
    }

    render() {
      const visible = this.expanded ? this.components : this.components.slice(0, this.previewCount);
      this.list.replaceChildren(...visible.map((item) => this.renderItem(item)));

      const hiddenCount = this.components.length - this.previewCount;
      this.toggle.hidden = hiddenCount <= 0;
      this.toggle.textContent = this.expanded ? 'Show fewer' : `Show all ${this.components.length} products`;

      let message = '';
      if (this.unavailableCount) message = 'This bundle is not available for your account.';
      else if (!this.components.length) message = 'This combination is not available.';
      else if (this.shortItems.length) {
        message = `Not enough stock for: ${this.shortItems.map((item) => item.title).join(', ')}`;
      }
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    renderItem(item) {
      const li = document.createElement('li');
      li.className = 'bundle-picker__item';
      if (this.shortItems.includes(item)) li.classList.add('bundle-picker__item--short');

      if (this.showImages) {
        const media = document.createElement('div');
        media.className = 'bundle-picker__media';
        if (item.image) {
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.width = 60;
          img.height = 60;
          img.loading = 'lazy';
          media.appendChild(img);
        }
        li.appendChild(media);
      }

      const text = document.createElement('div');
      text.className = 'bundle-picker__text';
      const title = document.createElement(item.url ? 'a' : 'span');
      if (item.url) title.href = item.url;
      title.className = item.url ? 'bundle-picker__title link link--text' : 'bundle-picker__title';
      title.textContent = item.title;
      text.appendChild(title);
      if (this.showSku && item.sku) {
        const sku = document.createElement('span');
        sku.className = 'bundle-picker__sku caption';
        sku.textContent = `SKU ${item.sku}`;
        text.appendChild(sku);
      }
      li.appendChild(text);

      const qty = document.createElement('span');
      qty.className = 'bundle-picker__qty';
      qty.textContent = `× ${item.quantity}`;
      li.appendChild(qty);
      return li;
    }

    writeProperties(selections) {
      const properties = [];
      Object.entries(selections).forEach(([name, value]) => properties.push([name, value]));
      // Contents first so it leads the property list in Admin. One item per
      // line; the ";" keeps it readable wherever the line breaks collapse.
      properties.push(['_bundle_contents', this.components
        .map((item) => `${item.quantity} × ${item.title}`)
        .join(';\n')]);
      properties.push(['_bundle', this.dataset.productId]);
      // Hidden items have no variant id here; b2b-app fills them in from the
      // bundle's saved contents.
      properties.push(['_bundle_items', this.components
        .filter((item) => item.variantId)
        .map((item) => `${item.variantId}:${item.quantity}`)
        .join(',')]);

      this.propertiesContainer.replaceChildren(...properties.map(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        input.setAttribute('form', this.dataset.formId);
        return input;
      }));
    }

    get submitButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`) || this.form?.querySelector('[type="submit"]');
    }

    // Only re-enable the button if this picker disabled it, so a sold-out tier
    // stays disabled by the theme's own variant logic.
    updateSubmitButton() {
      const button = this.submitButton;
      if (!button) return;
      const label = button.querySelector('span');
      if (!this.valid) {
        button.setAttribute('disabled', 'disabled');
        button.dataset.bundleDisabled = 'true';
        if (label) label.textContent = window.variantStrings?.unavailable || 'Unavailable';
      } else if (button.dataset.bundleDisabled) {
        button.removeAttribute('disabled');
        delete button.dataset.bundleDisabled;
        if (label) label.textContent = window.variantStrings?.addToCart || 'Add to cart';
      }
    }

    onSubmit(event) {
      this.update();
      if (this.valid) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.error.hidden = false;
      this.error.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}
